import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Constants } from '../helpers';
import { tap, map, switchMap } from 'rxjs/operators';
import {
	AddBucketData, AddBucketResponse,
	BucketData,
	FilterOptions, FilterOptionsResponse,
	FilterParameters, PaginateProjectsResponse,
	SalesPipelineTotals,
	SortParameters, UpdateDeleteBucketResponse,
} from '../interfaces/sales-pipeline';

type QueryParamsObject = Record<string, string | string[]>;

const FILTER_STORAGE_KEY = 'salesPipeline_filters';
const SORT_STORAGE_KEY = 'salesPipeline_sort';

@Injectable()
export class SalesPipelineService {
	private salesPipelineBuckets = new BehaviorSubject<BucketData[]>([]);
	public salesPipelineBuckets$ = this.salesPipelineBuckets.asObservable();

	// Filtering and sorting
	private filter = new BehaviorSubject<FilterParameters>({});
	public filter$ = this.filter.asObservable();

	private sort = new BehaviorSubject<SortParameters | null>(null);
	public sort$ = this.sort.asObservable();

	private isLoading = new BehaviorSubject<boolean>(false);
	public isLoading$ = this.isLoading.asObservable();

	private filterOptions = new BehaviorSubject<FilterOptions>(null);
	public filterOptions$ = this.filterOptions.asObservable();

	// Project totals
	private totals = new BehaviorSubject<SalesPipelineTotals>(null);
	public totals$ = this.totals.asObservable();

	constructor(private http: HttpClient) {
		this.loadFilterOptions();
		this.loadFiltersFromStorage();
		this.loadSortFromStorage();

		combineLatest([this.filter$, this.sort$])
			.pipe(
				switchMap(() => {
					return combineLatest([
						this.getSalesPipelineStructure(),
						this.getSalesPipelineTotals()
					]);
				})
			)
			.subscribe();
	}

	public addBucket(formData: AddBucketData): Observable<AddBucketResponse> {
		return this.http.post<AddBucketResponse>(`${Constants.BASE_API_URL}/bucket/add`, formData).pipe(
			tap(() => this.refreshData())
		);
	}

	public getSalesPipelineStructure(): Observable<any> {
		this.isLoading.next(true);

		const params = this.buildQueryParams();

		return this.http.get<any>(`${Constants.BASE_API_URL}/sales-pipeline/structure`, { params }).pipe(
			map(response => response.statusColumns || {}),
			tap((statusColumns) => {
				this.salesPipelineBuckets.next(statusColumns);
				this.isLoading.next(false);
			})
		);
	}

	public updateProjectBucket(projectId: string, updateData: { status: string; bucketId: string }): Observable<UpdateDeleteBucketResponse> {
		return this.http.patch<UpdateDeleteBucketResponse>(`${Constants.BASE_API_URL}/bucket/${projectId}/bucket`, updateData).pipe();
	}

	public updateBucket(updateData: { name: string, bucketId: string }): Observable<UpdateDeleteBucketResponse> {
		return this.http.patch<UpdateDeleteBucketResponse>(`${Constants.BASE_API_URL}/bucket/update`, updateData).pipe(
			tap(() => this.refreshData())
		);
	}

	public deleteBucket(bucketId: string): Observable<UpdateDeleteBucketResponse> {
		return this.http.delete<UpdateDeleteBucketResponse>(`${Constants.BASE_API_URL}/bucket/${bucketId}`).pipe(
			tap(() => this.refreshData())
		);
	}

	public paginateMoreProjectsByBucket(bucketId: string, hasParent: boolean = false, page: number = 1): Observable<PaginateProjectsResponse> {
		const filters = this.filter.value;
		const sort = this.sort.value;

		let params: QueryParamsObject = {
			bucketId: bucketId,
			page: page.toString()
		};

		if (hasParent) {
			params.hasParent = 'true';
		}

		Object.keys(filters).forEach((key) => {
			const v = filters[key as keyof FilterParameters];
			if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) return;
			params[key] = v;
		});

		if (sort) {
			params.sortBy = sort.field;
			params.sortOrder = sort.direction;
		}

		return this.http.get<PaginateProjectsResponse>(`${Constants.BASE_API_URL}/sales-pipeline/paginate`, { params }).pipe(
			map(response => {
				return {
					projects: response.projects,
					pagination: response.pagination
				};
			})
		);
	}

	public setFilters(filters: FilterParameters): void {
		this.filter.next(filters);
		this.saveFiltersToStorage(filters);
	}

	public clearFilters(): void {
		this.filter.next({});
		this.clearFiltersFromStorage();
	}

	public setSorting(sort: SortParameters | null): void {
		this.sort.next(sort);
		this.saveSortToStorage(sort);
	}

	public clearSorting(): void {
		this.sort.next(null);
		this.clearSortFromStorage();
	}

	private buildQueryParams(additionalParams: QueryParamsObject = {}): HttpParams {
		const filters = this.filter.value;
		const sort = this.sort.value;

		const params: QueryParamsObject = { ...additionalParams };

		Object.keys(filters).forEach((key) => {
			const v = filters[key as keyof FilterParameters];
			if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) return;
			params[key] = v;
		});

		if (sort) {
			params.sortBy = sort.field;
			params.sortOrder = sort.direction;
		}

		return new HttpParams({ fromObject: params });
	}

	public loadFilterOptions(): void {
		this.http.get<FilterOptionsResponse>(`${Constants.BASE_API_URL}/sales-pipeline/filter-options`)
			.pipe(
				map(response => ({
					estimators: response.estimators || [],
					salesPersons: response.salesPersons || [],
					projectManagers: response.projectManagers || [],
					contractAdministrators: response.contractAdministrators || [],
					states: response.states || [],
					divisions: response.divisions || [],
					companies: response.companies || [],
					clients: response.clients || [],
					statuses: response.statuses || []
				}))
			)
			.subscribe(options => {
				this.filterOptions.next(options);
			})
	}

	public getSalesPipelineTotals(): Observable<SalesPipelineTotals> {
		const params = this.buildQueryParams();

		return this.http.get<SalesPipelineTotals>(`${Constants.BASE_API_URL}/sales-pipeline/totals`, { params }).pipe(
			tap((totals) => {
				this.totals.next(totals);
			})
		)
	}

	public refreshData(): void {
		this.getSalesPipelineStructure().subscribe();
	}

	private saveFiltersToStorage(filters: FilterParameters): void {
		try {
			localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
		} catch (error) {
			console.error("Error saving filters to localStorage:", error);
		}
	}

	private loadFiltersFromStorage(): void {
		try {
			const savedFilters = localStorage.getItem(FILTER_STORAGE_KEY);
			if (savedFilters) {
				const filters = JSON.parse(savedFilters) as FilterParameters;
				this.filter.next(filters);
			}
		} catch (error) {
			console.error("Error loading filters from localStorage:", error);
			this.clearFiltersFromStorage();
		}
	}

	private clearFiltersFromStorage(): void {
		try {
			localStorage.removeItem(FILTER_STORAGE_KEY);
		} catch (error) {
			console.error("Error clearing filters from localStorage:", error);
		}
	}

	private saveSortToStorage(sort: SortParameters | null): void {
		try {
			if (sort) {
				localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(sort));
			} else {
				localStorage.removeItem(SORT_STORAGE_KEY);
			}
		} catch (error) {
			console.error("Error saving sort to localStorage:", error);
		}
	}

	private loadSortFromStorage(): void {
		try {
			const savedSort = localStorage.getItem(SORT_STORAGE_KEY);
			if (savedSort) {
				const sort = JSON.parse(savedSort) as SortParameters;
				this.sort.next(sort);
			}
		} catch (error) {
			console.error("Error loading sort from localStorage:", error);
			this.clearSortFromStorage();
		}
	}

	private clearSortFromStorage(): void {
		try {
			localStorage.removeItem(SORT_STORAGE_KEY);
		} catch (error) {
			console.error("Error clearing sort from localStorage:", error);
		}
	}
}